import React, { FC } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { SloDTO } from '@atlassiansox/phobos-client-ts';

import { PopupLink, ExternalLinkAction } from '@/components/primitive';
import { DAC_DOCS_INTRO_URL } from '@/constants';

import { formatPerformanceResult } from '../../common/utils';
import { SLO_CALCULATION_DECIMAL_PLACES } from '../../common/constants';

import * as styles from './styles';

type SLIHelperProps = Pick<SloDTO, 'performance'>;

const SLIHelper: FC<SLIHelperProps> = ({ performance }) => {
  const result = performance && performance.result;
  if (!result) return <></>;

  const sliDisplay: string = formatPerformanceResult(result);
  const sliCalculation: string = formatPerformanceResult(result, SLO_CALCULATION_DECIMAL_PLACES);
  const eqSign = sliDisplay === sliCalculation ? '=' : <>&asymp;</>;

  return (
    <PopupLink title="Show calculations" icon={<span css={styles.calculationIconStyles}>Σ</span>}>
      <p>
        <Lozenge>SLI</Lozenge> (performance) is calculated as:
      </p>
      <p />
      <div css={styles.equationStyles}>
        <div css={styles.equationRowStyles}>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>good events (28 days)</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>good</span>
              <span>+</span>
              <span>bad events (28 days)</span>
            </div>
          </div>
          <div>&#x2715;</div>
          <div>100 %</div>
          <div>=</div>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellStyles}>
              <span>{performance?.goodCount}</span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>{performance?.goodCount}</span>
              <span>+</span>
              <span>{performance?.badCount}</span>
            </div>
          </div>
          <div>%</div>
          <div>{eqSign}</div>
          <div>
            <span>
              <b>{sliDisplay}</b>
            </span>
            <span>&nbsp;%</span>
          </div>
        </div>
      </div>
      <p>
        Hence <b>SLI</b> is calculated with events data from a <b>28 days</b> rolling window.
        See&nbsp;
        <ExternalLinkAction
          text="docs"
          href={`${DAC_DOCS_INTRO_URL}glossary/#sli--service-level-indicator-`}
        />
      </p>
    </PopupLink>
  );
};

export default SLIHelper;
